"use client";

import { useMemo, useState } from "react";
import {
  ArrowDownUp,
  BarChart3,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleX,
  Download,
  RotateCcw,
  Search,
  SlidersHorizontal,
  TrendingUp,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { Button } from "./components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Slider } from "./components/ui/slider";
import sourceData from "./data/asistencia-comite-central.json";

type Registro = {
  nombre: string;
  representacion2024: string | null;
  porcentaje2024: number | null;
  porcentaje2025: number | null;
  porcentaje2026: number | null;
  promedioAniosDisponibles: number;
  observacion: string | null;
  vaReeleccion2026: boolean;
  resultadoReeleccion2026: "electo" | "no_electo" | null;
  candidaturas2026: Candidatura2026[];
};

type ResultadoEleccion2026 = {
  electo: boolean;
  votos: number | null;
  porcentajeVotos: number | null;
  alcanceVotos: "persona" | "lista" | "no_informado";
  nota: string;
};

type Candidatura2026 = {
  nombreCompleto: string;
  tipo:
    | "CC Nacional"
    | "Delegado/a regional"
    | "Representante regional"
    | "Representante de frente";
  espacio: string;
  lista?: string;
  region?: string;
  frente?: string;
  confianzaCoincidencia: "exacta" | "probable";
  notaCoincidencia?: string;
  resultado2026: ResultadoEleccion2026;
};

const data = sourceData as {
  metadata: typeof sourceData.metadata;
  registros: Registro[];
};

const PAGE_SIZE = 15;
const numberFormat = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const integerFormat = new Intl.NumberFormat("es-CL", {
  maximumFractionDigits: 0,
});
const electionPercentFormat = new Intl.NumberFormat("es-CL", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-CL");

const formatPercent = (value: number | null) =>
  value === null ? "—" : `${numberFormat.format(value)}%`;
const formatElectionPercent = (value: number) =>
  `${electionPercentFormat.format(value)}%`;

const average = (values: Array<number | null>) => {
  const available = values.filter((value): value is number => value !== null);
  return available.length
    ? available.reduce((sum, value) => sum + value, 0) / available.length
    : 0;
};

const tone = (value: number | null) => {
  if (value === null) return "bg-[#f0eef2] text-[#756d7b]";
  if (value >= 80) return "bg-[#dcefc8] text-[#315024]";
  if (value >= 60) return "bg-[#fff0c9] text-[#684d08]";
  return "bg-[#f9d6d3] text-[#7a2928]";
};

function PercentageCell({ value }: { value: number | null }) {
  return (
    <span
      className={`inline-flex min-w-[4.8rem] justify-center rounded-full px-2.5 py-1 text-xs font-bold tabular-nums ${tone(value)}`}
    >
      {formatPercent(value)}
    </span>
  );
}

function ReelectionBadge({ candidaturas }: { candidaturas: Candidatura2026[] }) {
  if (!candidaturas.length) return null;
  const probable = candidaturas.some(
    (candidatura) => candidatura.confianzaCoincidencia === "probable",
  );
  return (
    <span
      className={`inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.06em] ${
        probable
          ? "bg-[#fff0c9] text-[#735408]"
          : "bg-[#dcefc8] text-[#315024]"
      }`}
    >
      <RotateCcw className="h-3 w-3" />
      {probable ? "Coincidencia probable" : "Candidato/a a reelección"}
    </span>
  );
}

function CandidacyCell({ candidaturas }: { candidaturas: Candidatura2026[] }) {
  if (!candidaturas.length) {
    return <span className="text-xs text-[#98919c]">No figura en las listas revisadas</span>;
  }

  return (
    <div className="space-y-2">
      {candidaturas.map((candidatura) => (
        <div key={`${candidatura.tipo}-${candidatura.nombreCompleto}`}>
          <p className="text-xs font-bold text-[#473653]">{candidatura.tipo}</p>
          {candidatura.tipo === "CC Nacional" && (
            <p className="mt-0.5 text-xs leading-5 text-[#726979]">Lista · {candidatura.lista}</p>
          )}
          {candidatura.tipo === "Delegado/a regional" && (
            <p className="mt-0.5 text-xs leading-5 text-[#726979]">Territorio · {candidatura.region}</p>
          )}
          {candidatura.tipo === "Representante regional" && (
            <>
              <p className="mt-0.5 text-xs leading-5 text-[#726979]">Región · {candidatura.region}</p>
              <p className="text-xs leading-5 text-[#726979]">Lista · {candidatura.lista}</p>
            </>
          )}
          {candidatura.tipo === "Representante de frente" && (
            <p className="mt-0.5 text-xs leading-5 text-[#726979]">Frente · {candidatura.frente}</p>
          )}
          {candidatura.notaCoincidencia && (
            <p className="mt-1 text-[11px] leading-4 text-[#8a6208]">{candidatura.notaCoincidencia}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function ElectionResultCell({ candidaturas }: { candidaturas: Candidatura2026[] }) {
  if (!candidaturas.length) {
    return <span className="text-xs text-[#98919c]">—</span>;
  }

  return (
    <div className="space-y-2">
      {candidaturas.map((candidatura) => {
        const result = candidatura.resultado2026;
        const voteLabel =
          result.alcanceVotos === "lista"
            ? "votos de lista"
            : result.alcanceVotos === "persona"
              ? "votos personales"
              : null;

        return (
          <div key={`${candidatura.tipo}-${candidatura.nombreCompleto}-resultado`}>
            <span
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.06em] ${
                result.electo
                  ? "bg-[#dcefc8] text-[#315024]"
                  : "bg-[#f9d6d3] text-[#7a2928]"
              }`}
            >
              {result.electo ? (
                <BadgeCheck className="h-3.5 w-3.5" />
              ) : (
                <CircleX className="h-3.5 w-3.5" />
              )}
              {result.electo ? "Electo/a" : "No electo/a"}
            </span>
            {result.votos !== null && voteLabel ? (
              <p className="mt-1.5 text-xs font-bold tabular-nums text-[#463d4d]">
                {integerFormat.format(result.votos)} {voteLabel}
                {result.porcentajeVotos !== null && (
                  <span className="font-normal text-[#7b7380]"> · {formatElectionPercent(result.porcentajeVotos)}</span>
                )}
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-[#7b7380]">Votación individual no informada</p>
            )}
            <p className="mt-1 max-w-64 text-[11px] leading-4 text-[#8b8390]">{result.nota}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const [query, setQuery] = useState("");
  const [representation, setRepresentation] = useState("todas");
  const [availableYears, setAvailableYears] = useState("todos");
  const [candidacyType, setCandidacyType] = useState("todos");
  const [electionResult, setElectionResult] = useState("todos");
  const [minimumAverage, setMinimumAverage] = useState(0);
  const [sort, setSort] = useState("promedio-desc");
  const [page, setPage] = useState(1);

  const records = data.registros;
  const reelectionCount = records.filter((record) => record.vaReeleccion2026).length;
  const electedCount = records.filter(
    (record) => record.resultadoReeleccion2026 === "electo",
  ).length;
  const representations = useMemo(
    () =>
      Array.from(
        new Set(
          records.map(
            (record) => record.representacion2024 ?? "Sin representación 2024",
          ),
        ),
      ).sort((a, b) => a.localeCompare(b, "es")),
    [records],
  );

  const yearlyAverages = useMemo(
    () => [
      { year: "2024", value: average(records.map((item) => item.porcentaje2024)) },
      { year: "2025", value: average(records.map((item) => item.porcentaje2025)) },
      { year: "2026", value: average(records.map((item) => item.porcentaje2026)) },
    ],
    [records],
  );

  const distribution = useMemo(() => {
    const high = records.filter((item) => item.promedioAniosDisponibles >= 80).length;
    const medium = records.filter(
      (item) =>
        item.promedioAniosDisponibles >= 60 &&
        item.promedioAniosDisponibles < 80,
    ).length;
    return { high, medium, low: records.length - high - medium };
  }, [records]);

  const filtered = useMemo(() => {
    const normalizedQuery = normalize(query.trim());
    const result = records.filter((record) => {
      const recordRepresentation =
        record.representacion2024 ?? "Sin representación 2024";
      const years = [
        record.porcentaje2024,
        record.porcentaje2025,
        record.porcentaje2026,
      ].filter((value) => value !== null).length;
      const candidacySearch = record.candidaturas2026
        .flatMap((candidatura) => [
          candidatura.nombreCompleto,
          candidatura.tipo,
          candidatura.espacio,
          candidatura.lista ?? "",
        ])
        .join(" ");
      const matchesCandidacy =
        candidacyType === "todos" ||
        (candidacyType === "reeleccion" && record.vaReeleccion2026) ||
        (candidacyType === "sin-candidatura" && !record.vaReeleccion2026) ||
        record.candidaturas2026.some(
          (candidatura) => candidatura.tipo === candidacyType,
        );
      const matchesElectionResult =
        electionResult === "todos" ||
        record.resultadoReeleccion2026 === electionResult;

      return (
        (!normalizedQuery ||
          normalize(`${record.nombre} ${candidacySearch}`).includes(normalizedQuery)) &&
        (representation === "todas" || recordRepresentation === representation) &&
        (availableYears === "todos" || years === Number(availableYears)) &&
        matchesCandidacy &&
        matchesElectionResult &&
        record.promedioAniosDisponibles >= minimumAverage
      );
    });

    return result.sort((a, b) => {
      if (sort === "nombre-asc") return a.nombre.localeCompare(b.nombre, "es");
      if (sort === "promedio-asc")
        return a.promedioAniosDisponibles - b.promedioAniosDisponibles;
      if (sort === "2026-desc")
        return (b.porcentaje2026 ?? -1) - (a.porcentaje2026 ?? -1);
      if (sort === "resultado-desc") {
        const rank = { electo: 2, no_electo: 1 } as const;
        return (
          (rank[b.resultadoReeleccion2026 as keyof typeof rank] ?? 0) -
          (rank[a.resultadoReeleccion2026 as keyof typeof rank] ?? 0)
        );
      }
      return b.promedioAniosDisponibles - a.promedioAniosDisponibles;
    });
  }, [availableYears, candidacyType, electionResult, minimumAverage, query, records, representation, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleRecords = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );
  const activeFilters =
    Boolean(query) ||
    representation !== "todas" ||
    availableYears !== "todos" ||
    candidacyType !== "todos" ||
    electionResult !== "todos" ||
    minimumAverage > 0;

  const updateFilter = (callback: () => void) => {
    callback();
    setPage(1);
  };

  const clearFilters = () => {
    setQuery("");
    setRepresentation("todas");
    setAvailableYears("todos");
    setCandidacyType("todos");
    setElectionResult("todos");
    setMinimumAverage(0);
    setSort("promedio-desc");
    setPage(1);
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "asistencia-comite-central-2024-2026.json";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-[#f4f3ee] text-[#29242e]">
      <header className="overflow-hidden bg-[#3e2e5c] text-white">
        <div className="relative mx-auto max-w-[1500px] px-5 py-8 sm:px-8 lg:px-12 lg:py-11">
          <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[42px] border-[#55c2ee]/15" />
          <div className="pointer-events-none absolute bottom-0 right-1/3 h-24 w-24 translate-y-1/2 rounded-full bg-[#c8e56c]/10 blur-2xl" />
          <div className="relative flex flex-col justify-between gap-7 lg:flex-row lg:items-end">
            <div>
              <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[#bde8fb]">
                <BarChart3 className="h-4 w-4" />
                Datos de participación
              </p>
              <h1 className="max-w-4xl text-3xl font-semibold tracking-[-0.035em] sm:text-5xl">
                Asistencia al Comité Central
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[#e4dff0] sm:text-base">
                Comparación nominal de asistencia para 2024, 2025 y el período
                parcial de 2026, con candidaturas y resultados oficiales de la elección.
              </p>
            </div>
            <Button
              onClick={downloadJson}
              className="w-fit border border-white/20 bg-white/10 text-white hover:bg-white/20 focus-visible:ring-white"
            >
              <Download className="h-4 w-4" />
              Descargar JSON
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1500px] space-y-6 px-5 py-6 sm:px-8 lg:px-12 lg:py-9">
        <section aria-label="Resumen" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <article className="metric-card">
            <span className="metric-icon bg-[#e4dded] text-[#513d72]"><Users /></span>
            <div>
              <p className="metric-label">Personas registradas</p>
              <p className="metric-value">{records.length}</p>
            </div>
          </article>
          <article className="metric-card">
            <span className="metric-icon bg-[#dcefc8] text-[#315024]"><RotateCcw /></span>
            <div>
              <p className="metric-label">Candidatos a reelección</p>
              <p className="metric-value">{reelectionCount}</p>
            </div>
          </article>
          <article className="metric-card">
            <span className="metric-icon bg-[#d8eef7] text-[#156889]"><Trophy /></span>
            <div>
              <p className="metric-label">Electos nuevamente</p>
              <p className="metric-value">{electedCount} <span className="text-sm font-semibold text-[#8a8290]">de {reelectionCount}</span></p>
            </div>
          </article>
          {yearlyAverages.map((item, index) => (
            <article className="metric-card" key={item.year}>
              <span
                className={`metric-icon ${
                  index === 0
                    ? "bg-[#d8eef7] text-[#156889]"
                    : index === 1
                      ? "bg-[#e4efca] text-[#4d681b]"
                      : "bg-[#f7dfd2] text-[#9a4737]"
                }`}
              >
                <TrendingUp />
              </span>
              <div>
                <p className="metric-label">Promedio {item.year}</p>
                <p className="metric-value">{formatPercent(item.value)}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="grid gap-5 xl:grid-cols-[1.2fr_0.8fr]">
          <article className="panel p-5 sm:p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="eyebrow">Evolución general</p>
                <h2 className="section-title">Promedio de asistencia por año</h2>
              </div>
              <span className="rounded-full bg-[#f9e7df] px-3 py-1 text-xs font-bold text-[#894236]">
                2026 parcial
              </span>
            </div>
            <div className="flex h-48 items-end gap-5 rounded-2xl bg-[#f7f6f2] px-5 pb-4 pt-6 sm:gap-8 sm:px-8">
              {yearlyAverages.map((item, index) => (
                <div className="flex h-full flex-1 flex-col justify-end" key={item.year}>
                  <p className="mb-2 text-center text-sm font-bold tabular-nums text-[#463d4d]">
                    {formatPercent(item.value)}
                  </p>
                  <div className="flex flex-1 items-end rounded-t-xl bg-white/80">
                    <div
                      className={`w-full rounded-t-xl transition-all ${
                        index === 0
                          ? "bg-[#56bfe9]"
                          : index === 1
                            ? "bg-[#b7d85a]"
                            : "bg-[#ec8f78]"
                      }`}
                      style={{ height: `${item.value}%` }}
                    />
                  </div>
                  <p className="mt-2 text-center text-xs font-bold text-[#766f7d]">
                    {item.year}
                  </p>
                </div>
              ))}
            </div>
          </article>

          <article className="panel p-5 sm:p-6">
            <p className="eyebrow">Distribución</p>
            <h2 className="section-title">Promedio de años disponibles</h2>
            <div className="mt-7 space-y-5">
              {[
                { label: "Alta", hint: "80% o más", value: distribution.high, color: "bg-[#9cc547]" },
                { label: "Media", hint: "60% a 79,9%", value: distribution.medium, color: "bg-[#efc04f]" },
                { label: "Baja", hint: "Menos de 60%", value: distribution.low, color: "bg-[#e57b70]" },
              ].map((item) => (
                <div key={item.label}>
                  <div className="mb-2 flex items-baseline justify-between gap-3">
                    <p className="text-sm font-bold text-[#3b3440]">
                      {item.label} <span className="font-normal text-[#8a8390]">· {item.hint}</span>
                    </p>
                    <p className="text-sm font-bold tabular-nums">{item.value}</p>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-[#ebe8e3]">
                    <div
                      className={`h-full rounded-full ${item.color}`}
                      style={{ width: `${(item.value / records.length) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </article>
        </section>

        <section className="panel overflow-hidden">
          <div className="border-b border-[#e6e2e9] p-5 sm:p-6">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="eyebrow flex items-center gap-2"><SlidersHorizontal className="h-3.5 w-3.5" />Explorar registros</p>
                <h2 className="section-title">Filtrar y comparar personas</h2>
              </div>
              {activeFilters && (
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="h-4 w-4" /> Limpiar filtros
                </Button>
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7">
              <label className="filter-label">
                Buscar por nombre
                <span className="relative mt-1.5 block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b8490]" />
                  <input
                    value={query}
                    onChange={(event) => updateFilter(() => setQuery(event.target.value))}
                    placeholder="Ej. Camila"
                    className="h-11 w-full rounded-xl border border-[#d9d5df] bg-white pl-10 pr-3 text-sm outline-none transition placeholder:text-[#aaa3af] focus:border-[#6e5b8c] focus:ring-2 focus:ring-[#6e5b8c]/15"
                  />
                </span>
              </label>

              <label className="filter-label">
                Representación 2024
                <span className="mt-1.5 block">
                  <Select value={representation} onValueChange={(value) => updateFilter(() => setRepresentation(value))}>
                    <SelectTrigger aria-label="Representación 2024"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todas">Todas</SelectItem>
                      {representations.map((item) => <SelectItem value={item} key={item}>{item}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </span>
              </label>

              <label className="filter-label">
                Años con registro
                <span className="mt-1.5 block">
                  <Select value={availableYears} onValueChange={(value) => updateFilter(() => setAvailableYears(value))}>
                    <SelectTrigger aria-label="Años con registro"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="1">1 año</SelectItem>
                      <SelectItem value="2">2 años</SelectItem>
                      <SelectItem value="3">3 años</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
              </label>

              <label className="filter-label">
                Espacio electoral 2026
                <span className="mt-1.5 block">
                  <Select value={candidacyType} onValueChange={(value) => updateFilter(() => setCandidacyType(value))}>
                    <SelectTrigger aria-label="Espacio electoral 2026"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="reeleccion">Solo reelección</SelectItem>
                      <SelectItem value="CC Nacional">CC Nacional</SelectItem>
                      <SelectItem value="Delegado/a regional">Delegados regionales</SelectItem>
                      <SelectItem value="Representante regional">Representantes regionales</SelectItem>
                      <SelectItem value="Representante de frente">Representantes de frentes</SelectItem>
                      <SelectItem value="sin-candidatura">Sin candidatura</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
              </label>

              <label className="filter-label">
                Resultado elección 2026
                <span className="mt-1.5 block">
                  <Select value={electionResult} onValueChange={(value) => updateFilter(() => setElectionResult(value))}>
                    <SelectTrigger aria-label="Resultado elección 2026"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="electo">Electos</SelectItem>
                      <SelectItem value="no_electo">No electos</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
              </label>

              <label className="filter-label">
                Promedio mínimo: {minimumAverage}%
                <span className="mt-4 block px-1">
                  <Slider
                    aria-label="Promedio mínimo"
                    min={0}
                    max={100}
                    step={5}
                    value={[minimumAverage]}
                    onValueChange={([value]) => updateFilter(() => setMinimumAverage(value))}
                  />
                </span>
              </label>

              <label className="filter-label">
                Ordenar por
                <span className="mt-1.5 block">
                  <Select value={sort} onValueChange={(value) => updateFilter(() => setSort(value))}>
                    <SelectTrigger aria-label="Ordenar resultados"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promedio-desc">Mayor promedio</SelectItem>
                      <SelectItem value="promedio-asc">Menor promedio</SelectItem>
                      <SelectItem value="2026-desc">Mayor % 2026</SelectItem>
                      <SelectItem value="resultado-desc">Electos primero</SelectItem>
                      <SelectItem value="nombre-asc">Nombre A–Z</SelectItem>
                    </SelectContent>
                  </Select>
                </span>
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#e6e2e9] bg-[#faf9f7] px-5 py-3 text-sm sm:px-6">
            <p className="font-semibold text-[#514958]">
              <span className="text-[#3f2f5f]">{filtered.length}</span> resultados
              {filtered.length !== records.length && ` de ${records.length}`}
            </p>
            <p className="flex items-center gap-1.5 text-xs text-[#817984]">
              <ArrowDownUp className="h-3.5 w-3.5" /> Los porcentajes ausentes se muestran con —
            </p>
          </div>

          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full min-w-[1720px] border-collapse text-left">
              <thead className="bg-[#f1eef4] text-xs uppercase tracking-[0.08em] text-[#665c6d]">
                <tr>
                  <th className="table-head">Nombre</th>
                  <th className="table-head">Representación 2024</th>
                  <th className="table-head">Candidatura 2026</th>
                  <th className="table-head">Resultado elección 2026</th>
                  <th className="table-head text-center">% 2024</th>
                  <th className="table-head text-center">% 2025</th>
                  <th className="table-head text-center">% 2026</th>
                  <th className="table-head text-center">Promedio disponible</th>
                  <th className="table-head">Observación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#ece8ef]">
                {visibleRecords.map((record) => (
                  <tr className="transition-colors hover:bg-[#fbfafc]" key={record.nombre}>
                    <td className="table-cell">
                      <div className="flex min-w-44 flex-col gap-1.5">
                        <span className="font-semibold text-[#302839]">{record.nombre}</span>
                        <ReelectionBadge candidaturas={record.candidaturas2026} />
                      </div>
                    </td>
                    <td className="table-cell text-[#5d5562]">{record.representacion2024 ?? "Sin registro"}</td>
                    <td className="table-cell min-w-64"><CandidacyCell candidaturas={record.candidaturas2026} /></td>
                    <td className="table-cell min-w-72"><ElectionResultCell candidaturas={record.candidaturas2026} /></td>
                    <td className="table-cell text-center"><PercentageCell value={record.porcentaje2024} /></td>
                    <td className="table-cell text-center"><PercentageCell value={record.porcentaje2025} /></td>
                    <td className="table-cell text-center"><PercentageCell value={record.porcentaje2026} /></td>
                    <td className="table-cell text-center"><PercentageCell value={record.promedioAniosDisponibles} /></td>
                    <td className="table-cell max-w-64 text-xs leading-5 text-[#746c78]">{record.observacion ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="divide-y divide-[#e9e5eb] lg:hidden">
            {visibleRecords.map((record) => (
              <article className="p-5" key={record.nombre}>
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-bold text-[#302839]">{record.nombre}</h3>
                    <p className="mt-1 text-xs text-[#79717e]">{record.representacion2024 ?? "Sin representación 2024"}</p>
                    <div className="mt-2"><ReelectionBadge candidaturas={record.candidaturas2026} /></div>
                  </div>
                  <PercentageCell value={record.promedioAniosDisponibles} />
                </div>
                {record.candidaturas2026.length > 0 && (
                  <div className="mb-4 grid gap-3 rounded-xl border border-[#e3dde7] bg-[#faf8fb] p-3 sm:grid-cols-2">
                    <div><CandidacyCell candidaturas={record.candidaturas2026} /></div>
                    <div className="border-t border-[#e3dde7] pt-3 sm:border-l sm:border-t-0 sm:pl-3 sm:pt-0">
                      <ElectionResultCell candidaturas={record.candidaturas2026} />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ["2024", record.porcentaje2024],
                    ["2025", record.porcentaje2025],
                    ["2026", record.porcentaje2026],
                  ].map(([year, value]) => (
                    <div className="rounded-xl bg-[#f6f4f7] p-3 text-center" key={String(year)}>
                      <p className="text-[11px] font-bold text-[#8a8290]">{year}</p>
                      <p className="mt-1 text-sm font-bold tabular-nums text-[#413848]">{formatPercent(value as number | null)}</p>
                    </div>
                  ))}
                </div>
                {record.observacion && <p className="mt-3 text-xs leading-5 text-[#776f7b]">{record.observacion}</p>}
              </article>
            ))}
          </div>

          {visibleRecords.length === 0 && (
            <div className="flex flex-col items-center px-5 py-16 text-center">
              <Search className="mb-3 h-8 w-8 text-[#9b91a3]" />
              <h3 className="font-bold text-[#403748]">No hay coincidencias</h3>
              <p className="mt-1 text-sm text-[#7a7280]">Prueba reduciendo los filtros aplicados.</p>
              <Button className="mt-5" variant="secondary" onClick={clearFilters}>Limpiar filtros</Button>
            </div>
          )}

          {filtered.length > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 border-t border-[#e6e2e9] bg-[#faf9f7] px-5 py-4 sm:flex-row sm:px-6">
              <p className="text-xs text-[#7b7480]">
                Mostrando {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} de {filtered.length}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="secondary" className="h-9 px-3" disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <span className="min-w-20 text-center text-xs font-bold text-[#574e5e]">{currentPage} / {totalPages}</span>
                <Button variant="secondary" className="h-9 px-3" disabled={currentPage === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </section>

        <aside className="flex items-start gap-3 rounded-2xl border border-[#ddd4e4] bg-[#eee8f2] p-4 text-sm leading-6 text-[#584d60]">
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-[#5a426f]" />
          <div className="space-y-1.5">
            <p><strong>Asistencia:</strong> {data.metadata.criterio} Los datos de 2026 son parciales hasta el 9 de julio.</p>
            <p><strong>Reelección:</strong> {data.metadata.criterioReeleccion2026} {data.metadata.notaCoincidencias2026}</p>
            <p><strong>Resultados 2026:</strong> {data.metadata.criterioResultados2026} {data.metadata.notaVotos2026}</p>
          </div>
        </aside>
      </div>

      <footer className="mx-auto max-w-[1500px] px-5 pb-9 text-xs leading-5 text-[#7d7582] sm:px-8 lg:px-12">
        Fuentes: {data.metadata.fuente}, {data.metadata.fuenteCandidaturas2026} y {data.metadata.fuenteResultados2026}. {data.metadata.nota}
      </footer>
    </main>
  );
}
